/**
 * src/components/landing/MisDocumentosDrawer.tsx
 * =============================================================================
 * Panel flotante derecho con la lista de documentos del usuario.
 *
 * Diseño (2026-05-27): mismo lenguaje visual que la AnnotationToolbar — panel
 * flotante con `rounded-2xl shadow-md border` separado del borde. Sin
 * backdrop oscuro: el usuario sigue viendo la pizarra entera detrás, lo
 * que refuerza la sensación de "panel auxiliar" en vez de "modal bloqueante".
 *
 * Cierra al click fuera, Escape, o X. No bloquea scroll del fondo (la
 * landing usa fixed inset-0, así que no hay scroll de body de todos modos).
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, FileText, Loader2, Pencil, Pin, PinOff, Plus, Trash2, Upload, Users, X } from 'lucide-react'
import { BuscadorDocumentos, MINIMO_PARA_BUSCAR } from './BuscadorDocumentos'
import { formatBytes, getQuota, type QuotaInfo } from '@/services/billingService'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'
import { CreateSpaceModal } from '@/components/auth/CreateSpaceModal'
import { SpaceMembersModal } from '@/components/auth/SpaceMembersModal'
import { StorageBar } from '@/components/landing/StorageBar'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import {
  deleteDocumento,
  listDocumentos,
  pinDocumento,
  renameDocumento,
  type DocumentoListItem,
  type SharedDocumentoListItem,
} from '@/services/documentosService'

interface Props {
  open: boolean
  onClose: () => void
  /** Abre el selector único de PDF de la pizarra; no duplica la subida. */
  onUpload: () => void
}

export function MisDocumentosDrawer({ open, onClose, onUpload }: Props) {
  // i18n: el visor habla seis idiomas desde hoy; tener el panel solo en
  // español dejaba al usuario cambiando de idioma a mitad de su propia
  // aplicación. Cadenas en `panel.*` de cada `common.json`.
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { spaces, active, activeSpaceId, setActiveSpaceId } = useActiveSpace()
  const [quota, setQuota] = useState<QuotaInfo | null>(null)
  const [documentos, setDocumentos] = useState<DocumentoListItem[]>([])
  const [compartidos, setCompartidos] = useState<SharedDocumentoListItem[]>([])
  /** Carga en vuelo. Sin esto, entre abrir el drawer y llegar la respuesta
   *  se pintaba el vacío "Aún no hay documentos guardados" durante ~300ms
   *  y luego aparecían los documentos de golpe. Daba el susto de creer que
   *  se habían borrado. Arreglado 2026-08-14. */
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  /** Texto de búsqueda ya reposado. El filtrado ocurre EN EL SERVIDOR. */
  const [busqueda, setBusqueda] = useState('')
  /**
   * Cuántos documentos tiene el espacio SIN filtrar. Hace falta recordarlo
   * aparte: si se contara sobre la lista ya filtrada, buscar algo que devuelve
   * dos resultados escondería el propio buscador y dejaría al usuario atrapado
   * en una búsqueda que no puede borrar.
   */
  const [totalSinFiltrar, setTotalSinFiltrar] = useState(0)
  /** Dropdown del switcher abierto/cerrado. Aparece bajo el botón "Mi
   *  espacio" del header con la lista de espacios + acciones. */
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const asideRef = useRef<HTMLElement>(null)
  /** Último espacio para el que se vació la lista. Ver el efecto de carga. */
  const cambioDeEspacio = useRef<string | null | undefined>(undefined)
  const switcherRef = useRef<HTMLDivElement>(null)

  // Trampa de foco: aunque el drawer no tiene backdrop, para el teclado SÍ es
  // modal — Tab ciclaba antes por la pizarra invisible de detrás, y al cerrar
  // el foco se perdía en el body. Los modales anidados (crear espacio,
  // miembros) montan su propia trampa encima de esta (pila en useFocusTrap).
  useFocusTrap(asideRef, open)

  // Fetch quota + documentos en paralelo al abrir. Si falla algo, la
  // sección correspondiente simplemente no se pinta — el usuario sigue
  // pudiendo navegar e interactuar con el resto del drawer.
  // Re-fetcha también cuando cambia `activeSpaceId` (switcher de espacios).
  // Reseteamos las listas al cambiar de espacio para que el usuario NO
  // vea por ~200ms los docs del espacio anterior mientras llega la nueva
  // respuesta — confusión visual real reportada por la auditoría.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    // Reset solo al cambiar de ESPACIO, no al teclear en el buscador.
    //
    // Antes `busqueda` estaba en las dependencias de este mismo efecto, así que
    // cada pausa al escribir vaciaba la lista, la barra de almacenamiento y la
    // sección de compartidos, metía tres filas fantasma y las devolvía. El
    // comentario de `loading` dice que existe justo para no dar "el susto de
    // creer que se habían borrado"; al meter la búsqueda, ese susto volvía en
    // cada pausa al teclear.
    //
    // Ahora al buscar la lista se queda quieta y se sustituye cuando llegan los
    // resultados. Ver el `cambioDeEspacio` de abajo.
    if (cambioDeEspacio.current !== activeSpaceId) {
      cambioDeEspacio.current = activeSpaceId
      setDocumentos([])
      setCompartidos([])
      setQuota(null)
      setLoading(true)
    }
    void Promise.all([
      getQuota(activeSpaceId ?? undefined).catch(() => null),
      listDocumentos(activeSpaceId ?? undefined, busqueda),
    ]).then(([q, docs]) => {
      if (cancelled) return
      setQuota(q)
      setLoadError(false)
      setDocumentos(docs.documentos)
      setCompartidos(docs.compartidos)
      // El total solo se actualiza cuando NO hay búsqueda: es la foto del
      // espacio entero, no del resultado.
      if (!busqueda) setTotalSinFiltrar(docs.documentos.length)
      setLoading(false)
    }).catch(() => {
      if (cancelled) return
      // Un fallo de red nunca se representa como una lista vacía: eso hace
      // creer que los planos se han borrado cuando solo falta conexión.
      setLoadError(true)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open, activeSpaceId, busqueda, reloadKey])

  // Click fuera del switcher = cerrar el dropdown (sin cerrar el drawer).
  useEffect(() => {
    if (!switcherOpen) return undefined
    const onClick = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [switcherOpen])

  // Cerrar con Escape o click fuera del panel. Sin backdrop, escuchamos
  // mousedown a nivel de documento y comprobamos si el target está dentro
  // del aside. Idéntico patrón al UserMenu.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onMouseDown = (e: MouseEvent) => {
      if (asideRef.current && !asideRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    // Pequeño retraso para evitar que el mismo click que abre el drawer lo
    // cierre inmediatamente (el botón "Mis documentos" en LandingPage no
    // está dentro del aside, así que su click lo cerraría).
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown)
    }, 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [open, onClose])

  const handleClose = useCallback(() => onClose(), [onClose])

  if (!open) return null

  return (
    <aside
      ref={asideRef}
      role="dialog"
      // aria-modal pasa a true en coherencia con la trampa de foco: para un
      // lector de pantalla el fondo ya no es alcanzable con Tab.
      aria-modal="true"
      aria-label={t('panel.titulo')}
      // Panel flotante: separado del borde, redondeado, con sombra. Mismo
      // lenguaje que AnnotationToolbar. Sin backdrop — la pizarra detrás
      // permanece visible y nítida.
      className="fixed top-4 right-4 bottom-4 w-[min(380px,calc(100vw-2rem))] z-30 bg-white rounded-2xl shadow-md border border-zinc-200 flex flex-col overflow-hidden"
    >
      {/* Header: switcher de espacios + X. El switcher despliega la lista
          de espacios del user + acciones "Crear espacio" y "Miembros". */}
      <div className="flex items-center justify-between gap-2 px-3 h-12 border-b border-zinc-100 shrink-0">
        <div ref={switcherRef} className="relative">
          <button
            type="button"
            onClick={() => setSwitcherOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={switcherOpen}
            className="flex items-center gap-1.5 text-sm font-medium text-zinc-900 px-2 h-8 rounded-md hover:bg-zinc-100"
          >
            <span className="truncate max-w-[200px]">
              {active?.isPersonal ? t('panel.miEspacio') : (active?.name ?? t('panel.miEspacio'))}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-zinc-400 transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
              strokeWidth={1.8}
            />
          </button>

          {switcherOpen && (
            <div
              role="menu"
              className="absolute top-9 left-0 min-w-[260px] rounded-lg border border-zinc-200 bg-white shadow-lg py-1 z-40"
            >
              {/* Lista de espacios del user */}
              {spaces.length > 0 && (
                <div className="py-1">
                  <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-zinc-400 font-medium">
                    {t('panel.espacios')}
                  </p>
                  {spaces.map((sp) => {
                    const isActive = sp.id === active?.id
                    return (
                      <button
                        key={sp.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setActiveSpaceId(sp.id)
                          setSwitcherOpen(false)
                        }}
                        className="w-full text-left flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate">
                            {sp.isPersonal ? t('panel.miEspacio') : sp.name}
                          </p>
                          {!sp.isPersonal && (
                            <p className="text-[11px] text-zinc-500">
                              {t('panel.miembrosCount', { count: sp.memberCount })}
                              {sp.role === 'admin' ? ` · ${t('panel.rolAdmin')}` : ''}
                            </p>
                          )}
                        </div>
                        {isActive && (
                          <Check
                            className="h-3.5 w-3.5 shrink-0 text-zinc-900"
                            strokeWidth={2}
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Acciones: crear espacio + miembros (si admin no personal) */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setSwitcherOpen(false)
                  setCreateSpaceOpen(true)
                }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 border-t border-zinc-100"
              >
                <Plus className="h-4 w-4" strokeWidth={1.8} />
                {t('panel.crearEspacio')}
              </button>

              {active && !active.isPersonal && active.role === 'admin' && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSwitcherOpen(false)
                    setMembersOpen(true)
                  }}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 border-t border-zinc-100"
                >
                  <Users className="h-4 w-4" strokeWidth={1.8} />
                  {t('panel.miembros')}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label={t('panel.cerrar')}
          className="h-8 w-8 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
        >
          <X className="h-5 w-5" strokeWidth={1.8} />
        </button>
      </div>

      {/* Barra de almacenamiento. Mientras carga pintamos un hueco de la MISMA
          altura y forma: si no, el panel arrancaba sin barra, la barra
          aparecía de golpe al llegar la cuota y empujaba la lista entera
          hacia abajo. Combinado con el esqueleto de la lista, el resultado
          era una sucesión de saltos durante ~300ms.
          Si la petición de cuota falla, no pintamos nada (ni barra ni hueco):
          mejor un panel sin barra que un esqueleto eterno. */}
      <div className="px-3 pt-3 shrink-0">
        {/* El buscador solo aparece cuando hay documentos suficientes como
            para que haga falta. A quien tiene cuatro no le ayuda: le ocupa
            sitio y le sugiere que aquí hay algo que aprender. */}
        {(totalSinFiltrar >= MINIMO_PARA_BUSCAR || busqueda) && (
          <BuscadorDocumentos onBuscar={setBusqueda} buscando={loading} />
        )}

        {loading ? (
          <div
            className="rounded-lg border border-zinc-200 bg-white p-3 space-y-2"
            aria-hidden
          >
            <div className="flex items-baseline justify-between">
              <div className="h-3 w-24 rounded bg-zinc-100 animate-pulse" />
              <div className="h-3 w-20 rounded bg-zinc-100 animate-pulse" />
            </div>
            <div className="h-1.5 w-full rounded-full bg-zinc-100" />
            <div className="h-2.5 w-36 rounded bg-zinc-100 animate-pulse" />
          </div>
        ) : quota ? (
          <StorageBar quota={quota} space={active} />
        ) : null}

        {/* Acción primaria visible dentro del lugar donde el usuario gestiona
            sus documentos. En espacios bloqueados manda la CTA de activación
            de StorageBar; enseñar ambas acciones a la vez sería contradictorio. */}
        {!quota?.needsActivation && !quota?.ownerProMissing && (
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              onClose()
              onUpload()
            }}
            className="mt-3 w-full h-9 rounded-md bg-zinc-900 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-zinc-800 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" strokeWidth={1.8} />
            {t('panel.subirDocumento')}
          </button>
        )}
      </div>

      {/* Listas de documentos: propios y compartidos contigo */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide mb-2 px-1">
          {t('panel.titulo')}
        </p>

        {loading ? (
          /* Esqueleto mientras carga. Tres filas fantasma con la misma
             altura que una DocumentRow real, para que el panel no dé un
             salto cuando llegan los datos. Nunca el mensaje de vacío: decir
             "no hay documentos" antes de saberlo es mentir al usuario. */
          <ul className="space-y-1" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-3 px-2 py-2">
                <div className="h-4 w-4 rounded bg-zinc-100 shrink-0 animate-pulse" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div
                    className="h-3 rounded bg-zinc-100 animate-pulse"
                    style={{ width: `${70 - i * 15}%` }}
                  />
                  <div className="h-2 w-1/3 rounded bg-zinc-100 animate-pulse" />
                </div>
              </li>
            ))}
          </ul>
        ) : loadError ? (
          <div className="border border-zinc-200 rounded-lg p-6 text-center space-y-3 bg-zinc-50">
            <p className="text-sm text-zinc-600">{t('panel.cargaError')}</p>
            <button
              type="button"
              onClick={() => {
                setLoading(true)
                setReloadKey((key) => key + 1)
              }}
              className="text-xs font-medium text-zinc-900 underline underline-offset-2"
            >
              {t('panel.reintentar')}
            </button>
          </div>
        ) : documentos.length === 0 ? (
          <div className="border border-dashed border-zinc-200 rounded-lg p-6 text-center space-y-2 bg-zinc-50">
            <FileText className="h-6 w-6 text-zinc-400 mx-auto" strokeWidth={1.5} />
            {busqueda ? (
              <>
                {/* "No hay nada" y "no hay nada que coincida" son dos cosas muy
                    distintas para quien mira. Decir la primera cuando pasa la
                    segunda es lo que hace pensar que se han borrado. */}
                <p className="text-sm text-zinc-600">{t('panel.sinResultados')}</p>
                <p className="text-xs text-zinc-500">{t('panel.sinResultadosPista')}</p>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-600">{t('panel.vacio')}</p>
                <p className="text-xs text-zinc-500">
                  {t('panel.vacioPista')}
                </p>
              </>
            )}
          </div>
        ) : (
          <ul className="space-y-1">
            {documentos.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onOpen={() => {
                  onClose()
                  navigate(`/d/${d.id}`)
                }}
                onRenamed={(nuevoNombre) => {
                  setDocumentos((prev) =>
                    prev.map((x) => (x.id === d.id ? { ...x, nombre: nuevoNombre } : x)),
                  )
                }}
                onDeleted={() => {
                  setDocumentos((prev) => prev.filter((x) => x.id !== d.id))
                }}
                onPinned={(fijado) => {
                  // Reordenar en el sitio: los fijados arriba, y dentro de cada
                  // grupo por fecha. Es el mismo orden que aplica el servidor,
                  // replicado aquí para que el panel no dé un salto raro
                  // esperando a una recarga que no hace falta.
                  setDocumentos((prev) =>
                    prev
                      .map((x) => (x.id === d.id ? { ...x, fijado } : x))
                      .sort((a, b) => {
                        if (!!a.fijado !== !!b.fijado) return a.fijado ? -1 : 1
                        return b.updatedAt.localeCompare(a.updatedAt)
                      }),
                  )
                }}
              />
            ))}
          </ul>
        )}

        {/* Sección 'Compartidos contigo' — invitaciones nominales aceptadas
            de otros workspaces. Aparece SOLO si hay al menos uno, así no
            ensuciamos el panel de los usuarios solos. */}
        {compartidos.length > 0 && (
          <>
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide mt-5 mb-2 px-1">
              {t('panel.compartidos')}
            </p>
            <ul className="space-y-1">
              {compartidos.map((d) => (
                <SharedDocumentRow
                  key={d.id}
                  doc={d}
                  onOpen={() => {
                    onClose()
                    navigate(`/d/${d.id}`)
                  }}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Modales asociados al switcher del header. Se renderizan FUERA del
          aside flotante para que floten centrados sobre toda la pizarra
          (con backdrop) en lugar de quedar restringidos al ancho del drawer. */}
      {createSpaceOpen && (
        <CreateSpaceModal onClose={() => setCreateSpaceOpen(false)} />
      )}
      {membersOpen && active && !active.isPersonal && (
        <SpaceMembersModal space={active} onClose={() => setMembersOpen(false)} />
      )}
    </aside>
  )
}

// ---------------------------------------------------------------------------
// DocumentRow: fila individual con hover-actions (rename + delete).
// ---------------------------------------------------------------------------

interface DocumentRowProps {
  doc: DocumentoListItem
  onOpen: () => void
  onRenamed: (nuevoNombre: string) => void
  onDeleted: () => void
  /** Solo en los documentos propios: los compartidos contigo no se fijan. */
  onPinned?: (fijado: boolean) => void
}

/**
 * Fila de un documento en MisDocumentos. Tres modos de display:
 *  - Default: nombre + metadata; al hacer hover aparecen lápiz + papelera.
 *  - Renombrando: input en línea con el nombre, Enter guarda, Esc cancela.
 *  - Confirmando borrado: "¿Borrar? Sí / No" inline, sin modal porque rompe
 *    el flow del drawer (modal sobre drawer sobre pizarra = demasiadas capas).
 *
 * Click en el área principal abre el documento (`onOpen`). Los botones
 * de hover usan `stopPropagation` para no abrir cuando el user los pulsa.
 */
function DocumentRow({ doc, onOpen, onRenamed, onDeleted, onPinned }: DocumentRowProps) {
  // `i18n` además de `t` porque la fecha se formateaba siempre con 'es-ES':
  // un usuario alemán veía el resto en alemán y las fechas en formato español.
  const { t, i18n } = useTranslation()
  const [mode, setMode] = useState<'idle' | 'renaming' | 'confirming-delete'>('idle')
  // `displayName` quita ".pdf" del final — visualmente y en el draft del
  // rename. El nombre guardado en BD puede seguir conservando la extensión
  // (docs viejos) o no; el helper lo normaliza para que la UI sea uniforme.
  const [nombreDraft, setNombreDraft] = useState(displayName(doc.nombre))
  const [busy, setBusy] = useState(false)
  /**
   * Acción que acaba de fallar (renombrar/borrar), o null. Antes el fallo
   * solo iba a la consola: el nombre "volvía solo" al anterior o el
   * documento "se negaba" a borrarse sin ninguna explicación (auditoría
   * 2026-09-03). Se pinta como línea roja bajo la fila y se limpia al
   * intentar cualquier acción nueva.
   */
  const [errorAccion, setErrorAccion] = useState<'renombrar' | 'borrar' | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Al entrar en modo rename, focusear + seleccionar texto.
  useEffect(() => {
    if (mode !== 'renaming') return
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
    return () => cancelAnimationFrame(id)
  }, [mode])

  async function commitRename() {
    const trimmed = nombreDraft.trim()
    // No-op si el draft está vacío o coincide con el nombre actual (ya
    // sin extensión). Sin esta guarda enviaríamos un rename redundante.
    if (trimmed.length === 0 || trimmed === displayName(doc.nombre)) {
      setMode('idle')
      setNombreDraft(displayName(doc.nombre))
      return
    }
    setBusy(true)
    setErrorAccion(null)
    try {
      const applied = await renameDocumento(doc.id, trimmed)
      onRenamed(applied)
      setMode('idle')
    } catch (err) {
      console.error('rename failed', err)
      setNombreDraft(displayName(doc.nombre))
      setMode('idle')
      // El nombre acaba de "volver solo" al anterior: sin este aviso el
      // usuario no tiene forma de saber que el cambio no se guardó.
      setErrorAccion('renombrar')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    setBusy(true)
    setErrorAccion(null)
    try {
      await deleteDocumento(doc.id)
      onDeleted()
    } catch (err) {
      console.error('delete failed', err)
      setMode('idle')
      setBusy(false)
      setErrorAccion('borrar')
    }
  }

  // ── Modo renombrar ────────────────────────────────────────────────────
  if (mode === 'renaming') {
    return (
      <li className="rounded-md px-2 py-2 bg-zinc-50">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-zinc-400 shrink-0" strokeWidth={1.8} />
          <input
            ref={inputRef}
            type="text"
            value={nombreDraft}
            onChange={(e) => setNombreDraft(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void commitRename()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setNombreDraft(displayName(doc.nombre))
                setMode('idle')
              }
            }}
            disabled={busy}
            maxLength={120}
            className="flex-1 min-w-0 text-sm text-zinc-900 bg-white border border-zinc-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-zinc-300"
          />
          {busy && <Loader2 className="h-4 w-4 text-zinc-400 animate-spin" strokeWidth={2} />}
        </div>
      </li>
    )
  }

  // ── Modo confirmar borrado ────────────────────────────────────────────
  if (mode === 'confirming-delete') {
    return (
      <li className="rounded-md px-2 py-2 bg-red-50 border border-red-200">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-red-900 min-w-0 truncate">
            {t('panel.confirmarBorrado', { nombre: displayName(doc.nombre) })}
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setMode('idle')}
              disabled={busy}
              className="text-xs px-2 h-7 rounded text-zinc-700 hover:bg-white"
            >
              {t('panel.no')}
            </button>
            <button
              type="button"
              onClick={() => void confirmDelete()}
              disabled={busy}
              className="text-xs px-2 h-7 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5"
            >
              {busy && <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />}
              {t('panel.si')}
            </button>
          </div>
        </div>
      </li>
    )
  }

  // ── Modo idle (default con hover-actions) ─────────────────────────────
  return (
    <li className="group rounded-md hover:bg-zinc-100 transition-colors">
      <div className="flex items-center gap-2 px-2 py-2">
        <button
          type="button"
          onClick={onOpen}
          className="flex items-start gap-3 min-w-0 flex-1 text-left focus-visible:outline-none"
        >
          <FileText
            className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0"
            strokeWidth={1.8}
          />
          <div className="min-w-0 flex-1">
            {/* La chincheta se ve SIEMPRE en los fijados, no solo al pasar el
                ratón: si el único indicio de estar fijado fuera salir arriba,
                el usuario no tendría forma de saber por qué ese documento no
                se mueve de sitio. */}
            <p className="text-sm text-zinc-900 truncate flex items-center gap-1.5">
              {doc.fijado && (
                <Pin
                  className="h-3 w-3 text-zinc-400 shrink-0 fill-current"
                  strokeWidth={1.8}
                  aria-label={t('panel.fijado')}
                />
              )}
              {displayName(doc.nombre)}
            </p>
            {doc.currentVersion && (
              <p className="text-[11px] text-zinc-500 mt-0.5">
                v{doc.currentVersion.numero} · {formatBytes(doc.currentVersion.sizeBytes)} ·{' '}
                {new Date(doc.currentVersion.createdAt).toLocaleDateString(i18n.language)}
              </p>
            )}
            {/* Caducidad. Solo aparece si el documento caduca de verdad
                (cuentas free). Ámbar como el banner del visor, para que el
                usuario asocie los dos avisos. Que la caducidad se vea AQUÍ
                es lo que evita la sorpresa el día que el cron se lo lleve. */}
            {expiryLabel(doc.expiresAt, t) && (
              <p className="text-[11px] text-amber-700 mt-0.5">
                {expiryLabel(doc.expiresAt, t)}
              </p>
            )}
          </div>
        </button>
        {/* Iconos de acción: ocultos hasta hacer hover sobre la fila
            (`group-hover`), para no llenar el panel de botones — regla de
            producto: esconder antes que mostrar.
            `max-md:opacity-100` los deja SIEMPRE visibles en pantallas
            pequeñas: en táctil no existe el hover y, sin esto, renombrar y
            borrar eran inalcanzables desde el móvil. El comentario anterior
            decía que ya se hacía, pero no estaba implementado. */}
        <div className="flex items-center gap-0.5 opacity-0 max-md:opacity-100 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
          {onPinned && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                const nuevo = !doc.fijado
                // Se pinta el cambio antes de que responda el servidor: fijar
                // es reversible de un clic y esperar 200 ms a que suba la fila
                // hace que la chincheta parezca rota. Si la petición falla, el
                // panel se corrige solo en la siguiente apertura.
                onPinned(nuevo)
                void pinDocumento(doc.id, nuevo).catch(() => onPinned(!nuevo))
              }}
              aria-label={doc.fijado ? t('panel.soltar') : t('panel.fijar')}
              aria-pressed={!!doc.fijado}
              title={doc.fijado ? t('panel.soltar') : t('panel.fijar')}
              className="h-7 w-7 flex items-center justify-center rounded text-zinc-600 hover:bg-white hover:text-zinc-900 transition-colors"
            >
              {doc.fijado ? (
                <PinOff className="h-3.5 w-3.5" strokeWidth={1.8} />
              ) : (
                <Pin className="h-3.5 w-3.5" strokeWidth={1.8} />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setNombreDraft(displayName(doc.nombre))
              setErrorAccion(null)
              setMode('renaming')
            }}
            aria-label={t('panel.renombrar')}
            title={t('panel.renombrar')}
            className="h-7 w-7 flex items-center justify-center rounded text-zinc-600 hover:bg-white hover:text-zinc-900 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setErrorAccion(null)
              setMode('confirming-delete')
            }}
            aria-label={t('panel.borrar')}
            title={t('panel.borrar')}
            className="h-7 w-7 flex items-center justify-center rounded text-zinc-600 hover:bg-white hover:text-red-600 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>
      </div>
      {/* Aviso de la última acción fallida. Claves en los seis common.json. */}
      {errorAccion && (
        <p role="alert" className="px-2 pb-2 text-[11px] text-red-600">
          {errorAccion === 'renombrar'
            ? t('panel.errorRenombrar')
            : t('panel.errorBorrar')}
        </p>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// SharedDocumentRow: fila de un documento que NO es tuyo pero te han
// invitado a anotar. Como invitado solo puedes abrirlo (sin rename/delete
// hover-actions). Mostramos "Compartido por X" para que sepas de dónde
// viene la invitación.
// ---------------------------------------------------------------------------
function SharedDocumentRow({
  doc,
  onOpen,
}: {
  doc: SharedDocumentoListItem
  onOpen: () => void
}) {
  const { t } = useTranslation()
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left flex items-start gap-3 px-2 py-2 rounded-md hover:bg-zinc-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
      >
        <FileText className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-zinc-900 truncate">{displayName(doc.nombre)}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
            {doc.inviter
              ? t('panel.compartidoPor', { quien: doc.inviter })
              : t('panel.compartidos')}
            {doc.currentVersion ? ` · v${doc.currentVersion.numero}` : ''}
          </p>
        </div>
      </button>
    </li>
  )
}

// "plano.pdf" → "plano". El icono FileText ya dice que es un PDF; ver la
// extensión en cada fila es ruido visual. Match case-insensitive del final.
function displayName(name: string): string {
  return name.replace(/\.pdf$/i, '')
}

/**
 * Etiqueta de caducidad para la fila de un documento, o null si no procede.
 *
 * Devuelve null cuando `expiresAt` es null (cuentas Pro y documentos
 * anteriores al TTL: esos NO caducan) o cuando la fecha ya pasó — en ese caso
 * el documento está pendiente de que lo barra el cron y anunciar "caducó"
 * sobre algo que todavía se abre confundiría más que ayudar.
 *
 * Días en vez de fecha absoluta: transmite mejor la urgencia, que es de lo
 * que va el aviso. Mismo criterio que `FreeQuotaBanner`.
 */
function expiryLabel(
  expiresAt: string | null,
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string | null {
  if (!expiresAt) return null
  const ts = new Date(expiresAt).getTime()
  if (Number.isNaN(ts)) return null
  const ms = ts - Date.now()
  if (ms <= 0) return null
  const hours = Math.floor(ms / (1000 * 60 * 60))
  if (hours < 1) return t('panel.caducaMenosHora')
  // El plural lo decide i18next, no un ternario. En español y en inglés bastan
  // dos formas; en cuanto entre un idioma con reglas distintas, un ternario
  // escrito a mano diría cosas raras y nadie se enteraría.
  if (hours < 24) return t('panel.caducaHoras', { count: hours })
  return t('panel.caducaDias', { count: Math.round(hours / 24) })
}
